// src/pages/Admin.js

import React, { useCallback, useEffect, useRef, useState } from "react";
import Header from "./components/Header";
import Footer from "./components/Footer";
import { useToast } from "./ToastContext";
import ConfirmModal from "./components/ConfirmModal";

export default function Admin() {
  const { addToast } = useToast();
  const [status, setStatus] = useState("Loading...");
  const [newCount, setNewCount] = useState(null);
  const [lastRun, setLastRun] = useState(null);
  const [users, setUsers] = useState([]);
  const [runLog, setRunLog] = useState([]);
  const [runningImport, setRunningImport] = useState(false);
  const [actionsUrl, setActionsUrl] = useState("");
  const [expandedUsers, setExpandedUsers] = useState(new Set());
  const [userStats, setUserStats] = useState({});
  const [statsLoading, setStatsLoading] = useState({});
  const [deletingUser, setDeletingUser] = useState(null);
  const [patreonToggling, setPatreonToggling] = useState(null);
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null);
  const pendingUserDeletes = useRef({});

  const loadStatus = () => {
    fetch("/.netlify/functions/scrape-status")
      .then((res) => res.json())
      .then((data) => {
        setStatus(data.message);
        setLastRun(data.lastRun);
        setNewCount(data.newCount);
      })
      .catch(() => {
        setStatus("Could not load status.");
      });
  };

  const loadRunLog = () => {
    fetch("/scrape-log.json")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) setRunLog(data);
      })
      .catch(() => setRunLog([]));
  };

  const runImportNow = async () => {
    setRunningImport(true);
    setStatus("Starting manual import...");
    try {
      const res = await fetch("/.netlify/functions/trigger-import", {
        method: "POST",
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(
          data.error || data.details || "Failed to trigger import",
        );
      }
      setStatus(data.message || "Manual import triggered.");
      setActionsUrl(data.actionsUrl || "");

      // Poll a few times so admins can see status update without manual refresh.
      let attempts = 0;
      const interval = setInterval(() => {
        attempts += 1;
        loadStatus();
        loadRunLog();
        if (attempts >= 10) {
          clearInterval(interval);
        }
      }, 15000);
    } catch (err) {
      setActionsUrl("");
      setStatus(`Manual import failed: ${err.message}`);
    } finally {
      setRunningImport(false);
    }
  };

  useEffect(() => {
    fetch("/.netlify/functions/listUsers")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setUsers(data);
        } else {
          console.error("Expected an array, got:", data);
          setUsers([]);
        }
      })
      .catch((err) => {
        console.error("Error fetching users:", err);
        setUsers([]);
      });
  }, []);

  useEffect(() => {
    loadStatus();
  }, []);

  useEffect(() => {
    loadRunLog();
  }, []);

  const handleToggleUser = async (uid) => {
    const next = new Set(expandedUsers);
    if (next.has(uid)) {
      next.delete(uid);
      setExpandedUsers(next);
      return;
    }
    next.add(uid);
    setExpandedUsers(next);

    if (userStats[uid] !== undefined) return;

    setStatsLoading((prev) => ({ ...prev, [uid]: true }));
    try {
      const res = await fetch(`/.netlify/functions/getUserStats?uid=${uid}`);
      const data = await res.json();
      setUserStats((prev) => ({ ...prev, [uid]: data }));
    } catch (err) {
      setUserStats((prev) => ({ ...prev, [uid]: null }));
      addToast("Failed to load user stats", "error");
    } finally {
      setStatsLoading((prev) => ({ ...prev, [uid]: false }));
    }
  };

  const undoDeleteUser = useCallback((uid) => {
    const pending = pendingUserDeletes.current[uid];
    if (!pending) return;

    clearTimeout(pending.timeoutId);
    setUsers((prev) => {
      const exists = prev.some((u) => u.uid === pending.user.uid);
      if (exists) return prev;
      const next = [...prev];
      next.splice(pending.index, 0, pending.user);
      return next;
    });
    delete pendingUserDeletes.current[uid];
    addToast("User deletion cancelled", "info");
  }, [addToast]);

  const scheduleDeleteUser = useCallback((userToDelete) => {
    if (!userToDelete) return;
    const originalIndex = users.findIndex((u) => u.uid === userToDelete.uid);
    if (originalIndex === -1) return;

    setUsers((prev) => prev.filter((u) => u.uid !== userToDelete.uid));
    setExpandedUsers((prev) => {
      const next = new Set(prev);
      next.delete(userToDelete.uid);
      return next;
    });

    const timeoutId = setTimeout(async () => {
      setDeletingUser(userToDelete.uid);
      try {
        const res = await fetch("/.netlify/functions/deleteUser", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ uid: userToDelete.uid }),
        });
        if (res.ok) {
          addToast(`Removed user ${userToDelete.email || userToDelete.uid}`, "success");
        } else {
          const data = await res.json();
          setUsers((prev) => {
            const exists = prev.some((u) => u.uid === userToDelete.uid);
            if (exists) return prev;
            const next = [...prev];
            next.splice(originalIndex, 0, userToDelete);
            return next;
          });
          addToast(`Failed to delete user: ${data.error || "Unknown error"}`, "error");
        }
      } catch (err) {
        setUsers((prev) => {
          const exists = prev.some((u) => u.uid === userToDelete.uid);
          if (exists) return prev;
          const next = [...prev];
          next.splice(originalIndex, 0, userToDelete);
          return next;
        });
        addToast("Failed to delete user", "error");
      } finally {
        setDeletingUser(null);
        delete pendingUserDeletes.current[userToDelete.uid];
      }
    }, 5000);

    pendingUserDeletes.current[userToDelete.uid] = {
      timeoutId,
      user: userToDelete,
      index: originalIndex,
    };

    addToast(
      `Deleting ${userToDelete.email || "user"} in 5 seconds`,
      "warning",
      5000,
      {
        actionLabel: "Undo",
        onAction: () => undoDeleteUser(userToDelete.uid),
      }
    );
  }, [addToast, undoDeleteUser, users]);

  useEffect(() => {
    const pendingDeletesAtUnmount = pendingUserDeletes.current;
    return () => {
      Object.values(pendingDeletesAtUnmount).forEach((pending) => {
        clearTimeout(pending.timeoutId);
      });
    };
  }, []);

  const confirmDeleteUserNow = () => {
    if (!confirmDeleteUser) return;
    scheduleDeleteUser(confirmDeleteUser);
    setConfirmDeleteUser(null);
  };

  const handleTogglePatreon = async (uid, currentAccess) => {
    setPatreonToggling(uid);
    try {
      const res = await fetch("/.netlify/functions/setPatreonAccess", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid, access: !currentAccess }),
      });
      if (res.ok) {
        setUsers((prev) =>
          prev.map((u) =>
            u.uid === uid ? { ...u, patreonAccess: !currentAccess } : u
          )
        );
        addToast(
          currentAccess ? "Patreon access revoked" : "Patreon access granted",
          "success"
        );
      } else {
        const data = await res.json();
        addToast(`Failed to update Patreon access: ${data.error}`, "error");
      }
    } catch (err) {
      addToast("Failed to update Patreon access", "error");
    } finally {
      setPatreonToggling(null);
    }
  };

  const hubUsers = users
    .filter((u) => u.lastLogin)
    .sort((a, b) => b.lastLogin.localeCompare(a.lastLogin));

  return (
    <>
      <Header />

      <div className="app-container admin-page">
        <h1 className="admin-title">Admin</h1>

        <div className="admin-card">
          <h2>Import Controls</h2>
          <p className="admin-status">{status}</p>
          {lastRun && <p>Last run: {lastRun}</p>}
          {newCount !== null && <p>New tutorials added: {newCount}</p>}
          <button
            className="admin-run-btn"
            onClick={runImportNow}
            disabled={runningImport}
          >
            {runningImport ? "Starting..." : "Run Import Now"}
          </button>
          {actionsUrl && (
            <p className="admin-hint">
              Track progress in{" "}
              <a href={actionsUrl} target="_blank" rel="noreferrer">
                GitHub Actions
              </a>
              .
            </p>
          )}
        </div>

        <div className="admin-card">
          <h2>Import Log</h2>
          {runLog.length === 0 ? (
            <p>No import runs recorded yet.</p>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Timestamp (UTC)</th>
                    <th>Status</th>
                    <th>New entries</th>
                    <th>Images saved</th>
                    <th>Message</th>
                    <th>Imported items</th>
                  </tr>
                </thead>
                <tbody>
                  {runLog.map((entry, i) => (
                    <tr
                      key={i}
                      className={
                        entry.status === "error" ? "row-error" : "row-success"
                      }
                    >
                      <td>{entry.timestamp}</td>
                      <td className="status-cell">{entry.status}</td>
                      <td>{entry.newCount ?? "-"}</td>
                      <td>{entry.imageSavedCount ?? "-"}</td>
                      <td>{entry.message}</td>
                      <td>
                        {Array.isArray(entry.importedItems) &&
                        entry.importedItems.length > 0 ? (
                          <details>
                            <summary>
                              View ({entry.importedItems.length})
                            </summary>
                            <ul className="imported-items-list">
                              {entry.importedItems.map((item, idx) => (
                                <li key={`${item.url}-${idx}`}>
                                  <a
                                    href={item.url}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    {item.title || item.url}
                                  </a>
                                  {item.platform ? ` (${item.platform})` : ""}
                                </li>
                              ))}
                            </ul>
                          </details>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="admin-card">
          <h2>Hub Users ({hubUsers.length})</h2>
          <div className="admin-users-list">
            {hubUsers.map((u) => {
              const isExpanded = expandedUsers.has(u.uid);
              const stats = userStats[u.uid];
              const isLoading = statsLoading[u.uid];
              return (
                <div key={u.uid} className="admin-user-row">
                  <div
                    className="admin-user-header"
                    onClick={() => handleToggleUser(u.uid)}
                  >
                    <div className="admin-user-info">
                      <span className="admin-user-email">
                        {u.email || "No email"}
                      </span>
                      {u.displayName && (
                        <span className="admin-user-name">{u.displayName}</span>
                      )}
                      <span className="admin-user-joined">
                        Joined{" "}
                        {u.joinedAt
                          ? new Date(u.joinedAt).toLocaleDateString("en-GB")
                          : u.createdAt
                          ? new Date(u.createdAt).toLocaleDateString("en-GB")
                          : "unknown"}
                        {u.lastLogin && (
                          <> &middot; Last seen{" "}
                          {new Date(u.lastLogin).toLocaleDateString("en-GB")}</>
                        )}
                      </span>
                    </div>
                    <div
                      className="admin-user-actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className={`admin-patreon-toggle ${u.patreonAccess ? "granted" : ""}`}
                        onClick={() => handleTogglePatreon(u.uid, u.patreonAccess)}
                        disabled={patreonToggling === u.uid}
                        title={u.patreonAccess ? "Revoke Patreon access" : "Grant Patreon access"}
                      >
                        {patreonToggling === u.uid
                          ? "..."
                          : u.patreonAccess
                          ? "Patreon ✓"
                          : "Patreon"}
                      </button>
                      <button
                        className="admin-delete-btn"
                        onClick={() => setConfirmDeleteUser(u)}
                        disabled={deletingUser === u.uid}
                      >
                        {deletingUser === u.uid ? "Removing..." : "Remove"}
                      </button>
                      <span className="admin-expand-icon">
                        {isExpanded ? "▲" : "▼"}
                      </span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="admin-user-stats">
                      {isLoading ? (
                        <p className="admin-stats-loading">Loading...</p>
                      ) : !stats ? (
                        <p>Failed to load stats.</p>
                      ) : (
                        <div className="admin-stats-grid">
                          <div className="admin-stat-section">
                            <h4>Login History ({stats.logins.length})</h4>
                            {stats.logins.length === 0 ? (
                              <p>No logins recorded yet.</p>
                            ) : (
                              <ul className="admin-stat-list">
                                {[...stats.logins]
                                  .sort((a, b) =>
                                    b.timestamp.localeCompare(a.timestamp)
                                  )
                                  .map((l, i) => (
                                    <li key={i}>
                                      {new Date(l.timestamp).toLocaleString(
                                        "en-GB"
                                      )}
                                    </li>
                                  ))}
                              </ul>
                            )}
                          </div>

                          <div className="admin-stat-section">
                            <h4>
                              Clicks ({stats.clicks.length})
                            </h4>
                            {stats.clicks.length === 0 ? (
                              <p>No clicks recorded yet.</p>
                            ) : (
                              <div className="admin-table-wrap">
                                <table className="admin-table admin-clicks-table">
                                  <thead>
                                    <tr>
                                      <th>Title</th>
                                      <th>Platform</th>
                                      <th>Time</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {[...stats.clicks]
                                      .sort((a, b) =>
                                        b.timestamp.localeCompare(a.timestamp)
                                      )
                                      .slice(0, 100)
                                      .map((c, i) => (
                                        <tr key={i}>
                                          <td>
                                            <a
                                              href={c.url}
                                              target="_blank"
                                              rel="noreferrer"
                                            >
                                              {c.title || c.url}
                                            </a>
                                          </td>
                                          <td>{c.platform}</td>
                                          <td>
                                            {new Date(
                                              c.timestamp
                                            ).toLocaleString("en-GB")}
                                          </td>
                                        </tr>
                                      ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>

                          <div className="admin-stat-section">
                            <h4>Favourites ({stats.favourites.length})</h4>
                            {stats.favourites.length === 0 ? (
                              <p>No favourites.</p>
                            ) : (
                              <ul className="admin-stat-list">
                                {stats.favourites.map((f, i) => (
                                  <li key={i}>
                                    <a
                                      href={f.url}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      {f.name || f.title || f.id}
                                    </a>
                                    {f.platform && (
                                      <span className="admin-stat-tag">
                                        {f.platform}
                                      </span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>

                          <div className="admin-stat-section">
                            <h4>Collections ({stats.collections.length})</h4>
                            {stats.collections.length === 0 ? (
                              <p>No collections.</p>
                            ) : (
                              <ul className="admin-stat-list admin-collections-list">
                                {stats.collections.map((col, i) => {
                                  const items = col.tutorials || [];
                                  return (
                                    <li key={i} className="admin-collection-item">
                                      <details>
                                        <summary className="admin-collection-summary">
                                          {col.name}
                                          <span className="admin-stat-tag">
                                            {items.length} item
                                            {items.length !== 1 ? "s" : ""}
                                          </span>
                                        </summary>
                                        {items.length > 0 && (
                                          <ul className="admin-collection-urls">
                                            {items.map((tutUrl, j) => (
                                              <li key={j}>
                                                <a
                                                  href={tutUrl}
                                                  target="_blank"
                                                  rel="noreferrer"
                                                >
                                                  {tutUrl}
                                                </a>
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                      </details>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <ConfirmModal
        open={Boolean(confirmDeleteUser)}
        title="Delete this user?"
        message={
          confirmDeleteUser
            ? `You are about to remove ${confirmDeleteUser.email || "this user"}. Deletion starts after 5 seconds and can be undone from the toast.`
            : ""
        }
        confirmLabel="Delete user"
        cancelLabel="Cancel"
        destructive
        onConfirm={confirmDeleteUserNow}
        onCancel={() => setConfirmDeleteUser(null)}
      />
      <Footer />
    </>
  );
}
